import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useItemPrice } from "../config";
import {
  APIProvider,
  InfoWindow,
  Map as GoogleMap,
} from "@vis.gl/react-google-maps";
import { useFilteredItems } from "../hooks/use-filtered-items";
import {
  useDiscoverActions,
  useDiscoverCities,
  useDiscoverContext,
  useDiscoverMeta,
} from "../state/discover-context";
import type { DiscoverCity } from "../types";
import { useSelectedItem } from "../hooks/use-selected-item";
import { DetailSlideOver } from "./detail-slide-over";
import {
  API_KEY,
  CITY_ZOOM,
  COUNTRY_ZOOM,
  MAP_ID,
  MOROCCO_CENTER,
  VIEWPORT_SYNC_ZOOM,
  pillIcon,
} from "./map/brand-map";
import { styleForItem } from "./map/category-style";
import { BoundsReporter } from "./map/bounds-reporter";
import {
  ClusteredPins,
  FitBoundsController,
  MapClickCloser,
  ZoomNudgeController,
} from "./map/clustered-pins";

import { Icon } from "@majourneys/icons";
import { countsState } from "../lib/counts-state";
export function DiscoverStage() {
  const { t, i18n } = useTranslation();
  const itemPrice = useItemPrice();
  const { state, items, isLoadingItems, itemsError } = useDiscoverContext();
  const counts = countsState(items.length, isLoadingItems, itemsError);
  const { selectItem, setViewportBounds } = useDiscoverActions();
  const { city } = useDiscoverMeta();
  const cities = useDiscoverCities();
  // Retain the last resolved city so a transient cities re-fetch (lang change,
  // context value churn) doesn't strand `selectedCity` as undefined and
  // collapse `hasCity` → false → controllers re-fire toward MOROCCO_CENTER
  // mid-interaction (e.g. cluster click).
  const lastResolvedCityRef = useRef<DiscoverCity | null>(null);
  const liveResolvedCity = state.selectedCitySlug
    ? cities.find((c) => c.slug === state.selectedCitySlug) ?? null
    : null;
  if (state.selectedCitySlug === null) {
    lastResolvedCityRef.current = null;
  } else if (liveResolvedCity) {
    lastResolvedCityRef.current = liveResolvedCity;
  }
  const selectedCity =
    liveResolvedCity ??
    (state.selectedCitySlug && lastResolvedCityRef.current?.slug === state.selectedCitySlug
      ? lastResolvedCityRef.current
      : null);
  const hasCity = Boolean(selectedCity);
  const centerLat =
    selectedCity && typeof selectedCity.latitude === "number"
      ? selectedCity.latitude
      : hasCity && city
        ? city.latitude
        : MOROCCO_CENTER.lat;
  const centerLng =
    selectedCity && typeof selectedCity.longitude === "number"
      ? selectedCity.longitude
      : hasCity && city
        ? city.longitude
        : MOROCCO_CENTER.lng;
  const visibleItems = useFilteredItems();
  const selected = useSelectedItem();

  // Manual zoom +/- signal via a monotonic counter (ZoomNudgeController applies
  // the delta to the live camera). The map itself is framed by fit-to-pins.
  const [zoomNudge, setZoomNudge] = useState(0);
  const iframeZoom = hasCity ? CITY_ZOOM : COUNTRY_ZOOM;
  // Re-fit whenever the visible set changes: city pick, tab, visibility filter,
  // or item count. Kept coarse so a re-render alone doesn't re-fit.
  const fitKey = `${state.selectedCitySlug ?? "all"}|${state.activeTab}|${state.visibility}|${visibleItems.length}`;

  const [infoItemId, setInfoItemId] = useState<string | null>(null);
  useEffect(() => {
    setInfoItemId(null);
  }, [state.selectedCitySlug, state.activeTab, state.visibility]);
  const infoItem =
    infoItemId !== null ? visibleItems.find((i) => i.id === infoItemId) ?? null : null;

  const showDetail = state.isDetailOpen && selected;

  // Lock defaultCenter/defaultZoom to first-mount values. vis.gl re-evaluates
  // these props each render — passing fresh objects can re-seat the camera
  // mid-interaction. Live updates flow through FitBoundsController/ZoomNudgeController.
  const initialCenterRef = useRef({ lat: centerLat, lng: centerLng });
  const initialZoomRef = useRef(iframeZoom);

  return (
    <section className="mj-discover-stage" aria-label={t("discover.stage")}>
      {showDetail ? (
        <DetailSlideOver item={selected} open />
      ) : (
        <div
          className={`mj-discover-stage__map${state.isDetailOpen ? " is-detail-open" : ""}`}
        >
          {API_KEY ? (
            <APIProvider apiKey={API_KEY} region="MA" language={i18n.language.split("-")[0]}>
              <GoogleMap
                mapId={MAP_ID}
                colorScheme="LIGHT"
                defaultCenter={initialCenterRef.current}
                defaultZoom={initialZoomRef.current}
                gestureHandling="greedy"
                disableDefaultUI
                clickableIcons={false}
                className="mj-discover-stage__map-iframe"
              >
                <ZoomNudgeController nudge={zoomNudge} />
                <FitBoundsController
                  fitKey={fitKey}
                  items={visibleItems}
                  fallback={{ lat: centerLat, lng: centerLng }}
                  hasCity={hasCity}
                />
                <MapClickCloser onClose={() => setInfoItemId(null)} />
                <BoundsReporter
                  minZoom={VIEWPORT_SYNC_ZOOM}
                  onBounds={setViewportBounds}
                />
                <ClusteredPins
                  items={visibleItems}
                  infoItemId={infoItemId}
                  onSelect={setInfoItemId}
                />

                {infoItem &&
                  typeof infoItem.latitude === "number" &&
                  typeof infoItem.longitude === "number" && (
                    <InfoWindow
                      position={{ lat: infoItem.latitude, lng: infoItem.longitude }}
                      pixelOffset={[0, -36]}
                      onCloseClick={() => setInfoItemId(null)}
                      headerDisabled
                    >
                      <article className="mj-map-card mj-dk-frame">
                        {infoItem.image ? (
                          <div
                            className="mj-map-card__media"
                            style={{ backgroundImage: `url(${infoItem.image})` }}
                          >
                            {itemPrice(infoItem) ? (
                              <span className="mj-map-card__price-chip">
                                {itemPrice(infoItem)}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                        <div className="mj-map-card__body">
                          <header className="mj-map-card__head">
                            <h3 className="mj-map-card__title">{infoItem.title}</h3>
                          </header>
                          <p
                            className="mj-map-card__meta"
                            style={{ ["--cat" as string]: styleForItem(infoItem).color }}
                          >
                            <span className="mj-map-card__cat">
                              <Icon name={pillIcon(infoItem.segment)} aria-hidden="true" />
                              {infoItem.subtitle || infoItem.location}
                            </span>
                            {infoItem.subtitle ? (
                              <>
                                <span aria-hidden="true">·</span>
                                <span>{infoItem.location}</span>
                              </>
                            ) : null}
                          </p>
                          {infoItem.description ? (
                            <p className="mj-map-card__desc">{infoItem.description}</p>
                          ) : null}
                          <footer className="mj-map-card__foot">
                            <span className="mj-map-card__price">
                              {infoItem.mode === "bookable"
                                ? t("discover.pin.pay_on_arrival")
                                : ""}
                            </span>
                            <button
                              type="button"
                              className="mj-map-card__cta"
                              onClick={() => {
                                selectItem(infoItem.id);
                                setInfoItemId(null);
                              }}
                            >
                              {t("discover.pin.view_details")}
                              <Icon name="ri-arrow-right-line" className="rtl:-scale-x-100" aria-hidden="true" />
                            </button>
                          </footer>
                        </div>
                      </article>
                    </InfoWindow>
                  )}
              </GoogleMap>
            </APIProvider>
          ) : (
            <iframe
              title={t("discover.map_of", { city: selectedCity?.name ?? city?.name ?? "Morocco" })}
              // `www.google.com`, not `maps.google.com`. The site's frame-src
              // allows the former and not the latter, so on production this
              // iframe was refused outright and the map panel read "This
              // content is blocked. Contact the site owner to fix the issue."
              // Same embed, a host the policy already trusts — widening the
              // policy to accommodate a second hostname would be the wrong
              // repair. (Third time: the day map had this exact bug.)
              src={`https://www.google.com/maps?q=${centerLat},${centerLng}&z=${iframeZoom}&hl=${i18n.language.split("-")[0]}&output=embed`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="mj-discover-stage__map-iframe"
            />
          )}

          <div className="mj-discover-stage__zoom" aria-label={t("discover.map_zoom")}>
            <button
              type="button"
              onClick={() => setZoomNudge((z) => z + 1)}
              aria-label={t("discover.zoom_in")}
            >
              <Icon name="ri-add-line" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setZoomNudge((z) => z - 1)}
              aria-label={t("discover.zoom_out")}
            >
              <Icon name="ri-subtract-line" aria-hidden="true" />
            </button>
          </div>

          {/* Same rule as the phone pill: a bone while the catalogue loads,
              gone if it failed, never "0 picks" about a page still loading. */}
          {counts === "failed" ? null : (
            <div className="mj-discover-stage__count" aria-hidden="true" data-counts={counts}>
              <Icon name="ri-ticket-2-fill" />
              {counts === "loading" ? (
                <>
                  <span className="mj-discover-bone" style={{ width: 30 }} />
                  <span>{t("discover.picks_label")}</span>
                </>
              ) : (
                <span>{t("discover.picks_count", { count: visibleItems.length })}</span>
              )}
            </div>
          )}

          <div className="mj-discover-stage__caption">
            <span>{t("discover.hero_label")}</span>
            <strong>{t("discover.live_pins_caption")}</strong>
          </div>
        </div>
      )}
    </section>
  );
}
